import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import SimulatedBadge from "@/components/paper/SimulatedBadge";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import AuditLog from "@/pages/AuditLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";

const fmt = (n, d = 2) =>
  (n === null || n === undefined || isNaN(n)) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

const EMPTY_FORM = { symbol: "NIFTY", side: "BUY", qty: 1, entry_price: 0, exit_price: 0, pnl: 0, rationale: "" };
const fmtTs = (ts) => {
  try {
    return new Date(ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return String(ts).slice(0, 16); }
};

export default function Journal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "journal";
  const setTab = (t) => setSearchParams(t === "journal" ? {} : { tab: t });

  // All hooks must be declared before any conditional return
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [symbols, setSymbols] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    const { data } = await api.get("/journal");
    setItems(data.items);
  };

  useEffect(() => {
    if (tab !== "journal") return;
    load();
    api.get("/market/symbols").then((r) => {
      const list = r.data?.symbols || [];
      setSymbols(list);
      if (list.length > 0) setForm((f) => ({ ...f, symbol: list[0].symbol }));
    }).catch(() => {});
  }, [tab]);

  const Tabs = () => (
    <div className="border-b border-[var(--border)] px-6 flex gap-1">
      {["journal", "audit"].map((t) => (
        <button key={t} onClick={() => setTab(t)}
          className={`px-4 py-2.5 text-xs font-section tracking-wider border-b-2 transition-colors ${t === tab ? "border-[var(--accent)] txt-accent" : "border-transparent txt-muted hover:text-[var(--text-primary)]"}`}>
          {t === "journal" ? "Trade Journal" : "Audit Log"}
        </button>
      ))}
    </div>
  );

  if (tab === "audit") {
    return (
      <AppShell>
        <PageHeader overline="Records" title="Journal & Audit" />
        <Tabs />
        <AuditLog embedded />
      </AppShell>
    );
  }

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/journal", { ...form, qty: Number(form.qty), entry_price: Number(form.entry_price), exit_price: Number(form.exit_price), pnl: Number(form.pnl), request_ai: true });
      toast.success("Entry saved · AI commentary generated");
      setOpen(false);
      setForm({ ...EMPTY_FORM, symbol: symbols[0]?.symbol || "NIFTY" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <PageHeader overline="Records" title="Journal & Audit"
        description="Log your rationale and outcome. Claude Sonnet 4.5 tags the behaviour and coaches you on each trade."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="journal-new-btn" className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider">
                <Plus className="w-4 h-4 mr-2" /> NEW ENTRY
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-[var(--bg-surface)] border-[var(--border)] text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">New journal entry</DialogTitle>
              </DialogHeader>
              <div className="space-y-3" data-testid="journal-form">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="overline">Symbol</Label>
                    <select
                      data-testid="journal-symbol"
                      value={form.symbol}
                      onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                      className="mt-1 w-full rounded-none bg-[var(--bg-page)] border border-[var(--border)] text-white text-sm font-mono-data px-3 py-2 outline-none"
                    >
                      {symbols.map((s) => (
                        <option key={s.symbol} value={s.symbol}>{s.symbol}</option>
                      ))}
                      {symbols.length === 0 && <option value={form.symbol}>{form.symbol}</option>}
                    </select>
                  </div>
                  <div>
                    <Label className="overline">Side</Label>
                    <Input data-testid="journal-side" value={form.side} onChange={(e) => setForm({ ...form, side: e.target.value.toUpperCase() })} className="mt-1 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
                  </div>
                  <div>
                    <Label className="overline">Qty</Label>
                    <Input data-testid="journal-qty" type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="mt-1 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="overline">Entry</Label>
                    <Input data-testid="journal-entry" type="number" value={form.entry_price} onChange={(e) => setForm({ ...form, entry_price: e.target.value })} className="mt-1 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
                  </div>
                  <div>
                    <Label className="overline">Exit</Label>
                    <Input data-testid="journal-exit" type="number" value={form.exit_price} onChange={(e) => setForm({ ...form, exit_price: e.target.value })} className="mt-1 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
                  </div>
                  <div>
                    <Label className="overline">P&L ₹</Label>
                    <Input data-testid="journal-pnl" type="number" value={form.pnl} onChange={(e) => setForm({ ...form, pnl: e.target.value })} className="mt-1 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
                  </div>
                </div>
                <div>
                  <Label className="overline">Rationale</Label>
                  <Textarea data-testid="journal-rationale" value={form.rationale} onChange={(e) => setForm({ ...form, rationale: e.target.value })} className="mt-1 rounded-none bg-[var(--bg-page)] border-[var(--border)] min-h-[100px]" placeholder="Why did you take this trade?" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="journal-save-btn" onClick={submit} disabled={saving} className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {saving ? "SAVING…" : "SAVE + GET AI FEEDBACK"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <Tabs />
      <div className="p-8 space-y-4">
        {items.length === 0 && (
          <div className="panel p-10 text-center">
            <BookOpen className="w-8 h-8 mx-auto txt-muted mb-3" />
            <div className="font-display text-2xl mb-1">No entries yet</div>
            <p className="txt-muted text-sm">Log your first trade to get AI commentary from Claude Sonnet 4.5.</p>
          </div>
        )}
        {items.map((e) => (
          <div key={e.id} className="panel panel-hover p-5" data-testid={`journal-item-${e.id}`}>
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display text-2xl">{e.symbol}</span>
                  <span className={`overline ${e.side === "BUY" ? "txt-profit" : "txt-loss"}`}>{e.side} {e.qty}</span>
                  <span className="overline">@ ₹{fmt(e.entry_price)} → ₹{fmt(e.exit_price)}</span>
                  <span className={`font-mono-data text-sm ${e.pnl > 0 ? "txt-profit" : e.pnl < 0 ? "txt-loss" : "txt-muted"}`}>
                    P&L ₹{fmt(e.pnl)}
                  </span>
                  {e.mode && (
                    <span className={`text-[10px] font-section px-1.5 py-0.5 border ${
                      e.mode === "LIVE" ? "border-emerald-500 text-emerald-400" :
                      e.mode === "PAPER" ? "border-amber-500 text-amber-400" :
                      "border-zinc-600 txt-muted"
                    }`}>{e.mode}</span>
                  )}
                  {e.simulated ? <SimulatedBadge testid={`journal-simulated-${e.id}`} /> : null}
                  {e.exit_reason && (
                    <span className={`text-[10px] font-section px-1.5 py-0.5 border ${
                      ["stop", "trail", "bracket_stop"].includes(e.exit_reason) ? "border-red-500/60 txt-loss" :
                      ["t1", "t2", "bracket_target"].includes(e.exit_reason) ? "border-emerald-500/60 txt-profit" :
                      "border-zinc-600 txt-muted"
                    }`} title="Why the trade closed">{String(e.exit_reason).replace("_", " ").toUpperCase()}</span>
                  )}
                </div>
                {(e.entry_at || e.exit_at) && (
                  <div className="font-mono-data text-xs txt-muted mt-1">
                    {e.entry_at ? `in ${fmtTs(e.entry_at)}` : ""}{e.entry_at && e.exit_at ? " · " : ""}{e.exit_at ? `out ${fmtTs(e.exit_at)}` : ""}
                    {e.stop_loss ? ` · SL ₹${fmt(e.stop_loss)}` : ""}{e.target_price ? ` · T ₹${fmt(e.target_price)}` : ""}
                  </div>
                )}
                {e.rationale && <p className="text-sm mt-3 txt-secondary">{e.rationale}</p>}
                {e.ai_commentary && (
                  <div className="mt-4 border-l-2 border-blue-500 pl-4 py-1">
                    <div className="overline mb-1">AI coach (Claude)</div>
                    <p className="text-sm">{e.ai_commentary}</p>
                  </div>
                )}
                {e.ai_tags?.length > 0 && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {e.ai_tags.map((t) => (
                      <span key={`tag-${t}`} className="text-xs border border-[var(--border)] px-2 py-0.5 font-mono-data txt-secondary">{t}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="overline shrink-0">{e.created_at?.slice(0, 10)}</div>
            </div>
          </div>
        ))}
      </div>
    </AppShell>
  );
}
